/**
 * ExportImportButtons — Compact action buttons for Export/Import
 * Usage: Pass as `actions` prop to ListPageContainer
 */
import { Download, Upload, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface ExportImportButtonsProps {
    onExport: () => void;
    onDownloadTemplate?: () => void;
    onImportClick?: () => void;
    exportLoading?: boolean;
    exportOnly?: boolean;
    /** Ẩn nút Nhập Excel trên thanh chính khi đã chuyển sang menu phụ `secondaryActions` (`[··· Khác]`) */
    hideImport?: boolean;
    /** Extra actions (e.g. "Thêm mới" button) */
    children?: React.ReactNode;
}

export function ExportImportButtons({
    onExport,
    onDownloadTemplate: _onDownloadTemplate,
    onImportClick,
    exportLoading = false,
    hideImport = false,
    children,
}: ExportImportButtonsProps) {
    return (
        <TooltipProvider delayDuration={300}>
            <div data-slot="mobile-actions" className="mobile-actions-grid grid grid-cols-4 gap-1 w-full sm:flex sm:w-auto sm:items-center sm:gap-1.5 [&>button]:w-full sm:[&>button]:w-auto [&>button]:h-8 sm:[&>button]:h-7 [&>button]:text-[11px] sm:[&>button]:text-xs [&>button]:px-1.5 sm:[&>button]:px-2.5 [&>button]:min-w-0 [&_span]:truncate [&>a]:w-full sm:[&>a]:w-auto [&>a>button]:w-full sm:[&>a>button]:w-auto">
                {/* Export */}
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            data-testid="btn-export-excel"
                            variant="outline"
                            size="sm"
                            className="h-8 sm:h-7 text-[11px] sm:text-xs gap-1 px-1.5 sm:px-2.5 text-slate-600 font-medium w-full sm:w-auto justify-center shrink-0"
                            onClick={onExport}
                            disabled={exportLoading}
                        >
                            {exportLoading ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
                            ) : (
                                <Download className="h-3.5 w-3.5 shrink-0" />
                            )}
                            <span className="truncate">Xuất<span className="hidden sm:inline"> Excel</span></span>
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>Xuất danh sách ra Excel</TooltipContent>
                </Tooltip>

                {/* Import (Optional) */}
                {onImportClick && !hideImport && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-8 sm:h-7 text-[11px] sm:text-xs gap-1 px-1.5 sm:px-2.5 text-emerald-700 border-emerald-200 hover:bg-emerald-50 font-medium w-full sm:w-auto justify-center shrink-0"
                                onClick={onImportClick}
                            >
                                <Upload className="h-3.5 w-3.5 shrink-0" />
                                <span className="truncate">Nhập<span className="hidden sm:inline"> Excel</span></span>
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>Nhập dữ liệu từ Excel</TooltipContent>
                    </Tooltip>
                )}

                {/* Note: Download Template is rendered inside ImportDialog when importing, not on the main toolbar */}

                {/* Separator on desktop only */}
                {children && (
                    <div className="hidden sm:block h-4 w-px bg-slate-200 mx-0.5" />
                )}

                {/* Extra actions (e.g. Create button) */}
                {children}
            </div>
        </TooltipProvider>
    );
}
